import { Container, LinearProgress } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { FilterAppBar } from "components/FilterAppBar";
import { JobCalendarView } from "components/job/JobCalendarView";
import { JobList } from "components/job/JobList";
import { JobListSkeleton } from "components/job/JobListSkeleton";
import { JobMapView } from "components/job/JobMapView";
import { isAbortError } from "helpers";
import { useEffectOnce } from "helpers/useEffectOnce";
import { JobFilter, JobFilterStorageKey } from "models/JobFilter";
import { JobStatus } from "operations/schema/schema";
import { JobFilterDialog } from "pages/jobs/dialogs/JobFilterDialog";
import { NewJobsAlert } from "pages/jobs/jobDetails/components/NewJobsAlert";
import { FC, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setPage, setSelectedJob } from "store/root.store";
import { getCachePrefill, getPartsEngineer, getRequestableParts } from "store/slices/cache.store";
import { getMobileCalendar } from "store/slices/calendar.store";
import { clearHistoryState, setHistorySelectedJob } from "store/slices/history.store";
import {
  downloadJobs,
  getJobs,
  selectIncompleteJobs,
  selectJobFilterCount,
  selectLoadingJobs,
  setJobFilter,
  setSelectedJob as setVisitSelectedJob,
} from "store/slices/jobs.store";
import { setAppBar } from "store/slices/menu.store";
import { clearPlannerState, setPlannerSelectedJob } from "store/slices/planner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { ViewPreOrderedPartsDialog } from "./dialogs/ViewPreOrderedPartsDialog";
import { JobCalendarActions } from "./JobCalendarActions";
import { JobListActions } from "./JobListActions";

const sortingType = "job";

type JobsPageProps = {
  calendarView?: boolean;
  mapView?: boolean;
};

export const JobsPage: FC<JobsPageProps> = (props) => {
  const { calendarView, mapView } = props;
  const dispatch = useAppDispatch();
  const { loading: cacheLoading } = useAppSelector((s) => s.cache);
  const jobs = useAppSelector(selectIncompleteJobs, shallowEqual);
  const loadingJobs = useAppSelector(selectLoadingJobs);
  const intl = useIntl();
  const { state }: any = useLocation();
  const openFilter: boolean = state?.openFilter ?? false;
  const [filterDialogOpen, setFilterDialogOpen] = useState(openFilter);
  const filterCount = useAppSelector(selectJobFilterCount);

  useEffectOnce(() => {
    const jobFilterString = localStorage.getItem(JobFilterStorageKey);
    let storedJobFilter: JobFilter | null = null;
    if (jobFilterString) {
      storedJobFilter = JSON.parse(jobFilterString) as JobFilter;
      if (storedJobFilter.specificDate)
        storedJobFilter.specificDate = new Date(storedJobFilter.specificDate);
    }
    if (storedJobFilter != null) {
      dispatch(setJobFilter({ jobFilter: storedJobFilter, hideNewJobNotification: false }));
    }
  });

  useEffect(() => {
    dispatch(setPage({ page: "jobs" }));
    dispatch(setSelectedJob({ jobId: undefined }));
    dispatch(setVisitSelectedJob({ jobId: undefined }));
    dispatch(setPlannerSelectedJob({ jobId: undefined }));
    dispatch(setHistorySelectedJob({ jobId: undefined }));
    dispatch(clearPlannerState());
    dispatch(clearHistoryState());
    dispatch(getCachePrefill({ force: false }));
    dispatch(getPartsEngineer({ force: false }));
    dispatch(getRequestableParts({ force: false }));
  }, [dispatch]);

  const dispatchGetJobs = useCallback(
    (override: boolean = false) => {
      dispatch(getJobs({ force: override }))
        .then(unwrapResult)
        .catch((e) => {
          if (isAbortError(e)) return;
          dispatch(addSnackbarMessage({ key: "GetJobs-fail" }));
        })
        .then(() => {
          if (jobs.length > 0) {
            var jobIds: string[] = [];
            jobs.forEach((job) => {
              if (job.status === JobStatus.Assigned) {
                jobIds.push(job.id);
              }
            });
            if (jobIds.length > 0) {
              dispatch(
                downloadJobs({
                  jobIds,
                })
              );
            }
          }
        });

      if (calendarView) {
        dispatch(getMobileCalendar());
      }
    },
    [calendarView, dispatch, jobs]
  );

  useEffect(() => {
    dispatchGetJobs(false);
  }, [dispatchGetJobs]);

  useEffect(() => {
    dispatch(
      setAppBar({
        title: intl.formatMessage({ id: "menu.myJobs" }),
        sortingType,
        refetchEnabled: true,
        calendarView: calendarView,
        unusedPartsButton: true,
      })
    );
  }, [dispatch, intl, calendarView]);

  return loadingJobs ||
    cacheLoading.prefill ||
    cacheLoading.engineerParts ||
    cacheLoading.requestableParts ? (
    <Container data-testid="jobsPageContainer">
      <LinearProgress />
      <JobListSkeleton showBorder />
    </Container>
  ) : (
    <>
      <FilterAppBar
        onClick={setFilterDialogOpen}
        foundCount={jobs.length}
        filterCount={filterCount}
        type="jobs"
      />
      <NewJobsAlert />
      <Container data-testid="jobsPageContainer" style={mapView ? { padding: 0 } : {}}>
        {calendarView && (
          <>
            <JobCalendarView
              jobs={jobs}
              showAvailability
              showSalesTasks
              showProjectTasks
              loading={loadingJobs}
              refetchJobs={dispatchGetJobs}
              toLink="/jobs"
            />
            <JobCalendarActions />
          </>
        )}
        {mapView && (
          <>
            <JobMapView
              jobs={jobs}
              loading={loadingJobs}
              refetchJobs={dispatchGetJobs}
              toLink="/jobs"
            />
            <JobListActions />
          </>
        )}
        {!calendarView && !mapView && (
          <>
            <JobList
              jobs={jobs}
              loading={loadingJobs}
              refetchJobs={dispatchGetJobs}
              toLink="/jobs"
              sortingType={sortingType}
              showBorder
            />
            <JobListActions />
          </>
        )}

        <JobFilterDialog
          open={filterDialogOpen}
          setOpen={setFilterDialogOpen}
          onClose={(dirty) => {
            dispatchGetJobs(dirty);
          }}
        />
        <ViewPreOrderedPartsDialog />
      </Container>
    </>
  );
};
